// Tipos para o sistema de controle de acesso baseado em roles

/**
 * Roles disponíveis no sistema
 */
export type UserRole =
  | 'admin'
  | 'operator'
  | 'viewer'
  | 'ti'
  | 'vendedor'
  | 'vendedor_interno';

/** Roles de vendas com isolamento por vendedor_id */
export const VENDEDOR_SCOPED_ROLES: readonly UserRole[] = [
  'vendedor',
  'vendedor_interno',
] as const;

export function isVendedorScopedRole(role: UserRole | null | undefined): boolean {
  return role === 'vendedor' || role === 'vendedor_interno';
}

export function isVendedorExternoRole(role: UserRole | null | undefined): boolean {
  return role === 'vendedor';
}

export function isVendedorInternoRole(role: UserRole | null | undefined): boolean {
  return role === 'vendedor_interno';
}

/**
 * Interface para o perfil do usuário com controle de acesso
 */
export interface UserProfile {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
  user_role: UserRole;
  setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
  ativo: boolean;
  telefone?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Interface para as permissões do usuário
 */
export interface UserPermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
  canManageFinance: boolean;
}

/**
 * Função para verificar se um usuário tem uma determinada permissão
 */
export function hasPermission(userRole: UserRole, requiredRole: UserRole): boolean {
  const roleHierarchy: Record<UserRole, number> = {
    'ti': 5, // TI tem acesso total (maior que admin)
    'admin': 4,
    'operator': 3,
    'viewer': 2,
    'vendedor': 2, // vendedor externo — nível similar ao viewer
    'vendedor_interno': 2,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Função para obter as permissões de um usuário baseado no seu role
 */
export function getUserPermissions(userRole: UserRole): UserPermissions {
  // TI tem acesso total a tudo
  const isTi = userRole === 'ti';
  return {
    canView: isTi || hasPermission(userRole, 'viewer'),
    canEdit: isTi || hasPermission(userRole, 'operator'),
    canDelete: isTi || hasPermission(userRole, 'admin'),
    canManageUsers: isTi || hasPermission(userRole, 'admin'),
    canManageSettings: isTi || hasPermission(userRole, 'admin'),
    canManageFinance: isTi || hasPermission(userRole, 'operator')
  };
}

/**
 * Função para obter o nome amigável de um role
 */
export function getRoleDisplayName(role: UserRole): string {
  const roleNames: Record<UserRole, string> = {
    'ti': 'Super Admin (TI)',
    'admin': 'Administrador',
    'operator': 'Operador',
    'viewer': 'Visualizador',
    'vendedor': 'Vendedor Externo',
    'vendedor_interno': 'Vendedor Interno',
  };
  return roleNames[role];
}

/**
 * Função para obter a cor do badge de um role
 */
export function getRoleBadgeColor(role: UserRole): string {
  const roleColors: Record<UserRole, string> = {
    'ti': 'bg-purple-100 text-purple-800',
    'admin': 'bg-red-100 text-red-800',
    'operator': 'bg-blue-100 text-blue-800',
    'viewer': 'bg-gray-100 text-gray-800',
    'vendedor': 'bg-amber-100 text-amber-800',
    'vendedor_interno': 'bg-orange-100 text-orange-800',
  };
  return roleColors[role];
}
